import ChecklistTemplatesPage from './pages/checklist-templates.tsx';
import MyJobsPage, { CrewJobPage } from './pages/my-jobs.tsx';
import StaffPreferencesPage from './pages/staff-preferences.tsx';
import StaffPage from './pages/staff.tsx';
import StaffTimeOffPage from './pages/staff-time-off.tsx';
import StaffSwapsPage from './pages/staff-swaps.tsx';
import SwapApprovalPage from './pages/swap-approval.tsx';
import StaffManagementPage from './pages/staff-management.tsx';
import StaffDocumentsPage from './pages/staff-documents.tsx';
import StaffVisitOffersPage from './pages/staff-visit-offers.tsx';
import StaffPhotosPage from './pages/staff-photos.tsx';
import CrewAccountPage from './pages/crew-account.tsx';
import LiveMapPage from './pages/live-map.tsx';
import ChatsPage from './pages/chats.tsx';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from './state/session.tsx';
import { companyHasPlanFeature } from './components/plan-gate.tsx';
import HelpPage from './pages/help.tsx';
import AppLayout from './components/app-layout.tsx';
import { LoadingBlock } from './components/ui.tsx';
import LoginPage, { sameOriginPath } from './pages/login.tsx';
import RegisterPage from './pages/register.tsx';
import CompleteSignupPage from './pages/complete-signup.tsx';
import ForgotPasswordPage from './pages/forgot-password.tsx';
import DeleteAccountPage from './pages/delete-account.tsx';
import ResetPasswordPage from './pages/reset-password.tsx';
import QuotesPage from './pages/quotes.tsx';
import NewQuotePage from './pages/new-quote.tsx';
import QuoteEditorPage from './pages/quote-editor.tsx';
import InvoicesPage from './pages/invoices.tsx';
import ChangeOrdersPage from './pages/change-orders.tsx';
import InspectionsPage from './pages/inspections.tsx';
import InspectionDetailPage from './pages/inspection-detail.tsx';
import CustomersPage from './pages/customers.tsx';
import MessagesPage from './pages/messages.tsx';
import InboxApprovalPage from './pages/inbox-approval.tsx';
import CustomerFollowUpsPage from './pages/customer-follow-ups.tsx';
import CustomerDetailPage from './pages/customer-detail.tsx';
import PropertyChecklistPage from './pages/property-checklist.tsx';
import CustomerImportPage from './pages/customer-import.tsx';
import SchedulePage from './pages/schedule.tsx';
import TodayPage from './pages/today.tsx';
import PlanningAvailabilityPage from './pages/planning-availability.tsx';
import ChangeRequestsPage from './pages/change-requests.tsx';
import OpenSpotPage from './pages/open-spot.tsx';
import NewSeriesPage from './pages/new-series.tsx';
import SeriesDetailPage from './pages/series-detail.tsx';
import SettingsPage from './pages/settings.tsx';
import TemplatesHubPage from './pages/settings/templates-hub.tsx';
import TeamHubPage from './pages/settings/team-hub.tsx';
import TeamSavedPage from './pages/settings/team-saved.tsx';
import TeamSwapPolicyPage from './pages/settings/team-swap-policy.tsx';
import TeamPayPage from './pages/settings/team-pay.tsx';
import AccountPage from './pages/settings/account.tsx';
import SecurityPage from './pages/settings/security.tsx';
import ConnectedAccountsPage from './pages/settings/connected-accounts.tsx';
import IntegrationsPage from './pages/settings/integrations.tsx';
import SettingsLeafPage from './pages/settings-leaf.tsx';
import ApiDocsPage from './pages/api-docs.tsx';
import PricingHubPage from './pages/settings/pricing-hub.tsx';
import PricingBasePage from './pages/settings/pricing-base.tsx';
import PricingAreaPage from './pages/settings/pricing-area.tsx';
import PricingAddOnsPage from './pages/settings/pricing-add-ons.tsx';
import PricingDustPage from './pages/settings/pricing-dust.tsx';
import PricingWalkthroughPage from './pages/settings/pricing-walkthrough.tsx';
import PricingNumberingPage from './pages/settings/pricing-numbering.tsx';
import MessagingHubPage from './pages/settings/messaging-hub.tsx';
import TextingSettingsPage from './pages/settings/texting.tsx';
import CallsSettingsPage from './pages/settings/calls.tsx';
import JobChatSettingsPage from './pages/settings/job-chat.tsx';
import DashboardPage from './pages/dashboard.tsx';
import ReportsPage from './pages/reports.tsx';
import MarginOverridesPage from './pages/margin-overrides.tsx';
import ReportSectionPage from './pages/reports/report-shell.tsx';
import RequestsPage from './pages/requests.tsx';
import WalkthroughsPage from './pages/walkthroughs.tsx';
import PhotoQuotesPage from './pages/photo-quotes.tsx';
import PhotoQuoteDemoPage from './pages/photo-quote-demo.tsx';
import PhotoQuoteReviewPage from './pages/photo-quote-review.tsx';
import MagicQuoteWalkthroughPage from './pages/magic-quote-walkthrough.tsx';
import MagicQuoteReviewPage from './pages/magic-quote-review.tsx';
import LogsPage from './pages/logs.tsx';
import WorkHubPage from './pages/work-hub.tsx';
import NotificationsPage from './pages/notifications.tsx';
import FeedbackPage from './pages/feedback.tsx';
import FeedbackDetailPage from './pages/feedback-detail.tsx';
import WorkOrderPage from './pages/work-order.tsx';
import NotFoundPage from './pages/not-found.tsx';
import AdminLayout, { RequireAdmin } from './components/admin/admin-layout.tsx';
import AdminLoginPage from './pages/admin/login.tsx';
import AdminHomePage from './pages/admin/home.tsx';
import AdminAdminsPage from './pages/admin/admins.tsx';
import AdminSecurityPage from './pages/admin/security.tsx';
import AdminCompaniesPage from './pages/admin/companies.tsx';
import AdminCompanyDetailPage from './pages/admin/company-detail.tsx';
import AdminDiscountsPage from './pages/admin/discounts.tsx';
import AdminFeedbackPage from './pages/admin/feedback.tsx';
import AdminFeedbackDetailPage from './pages/admin/feedback-detail.tsx';
import AdminFeedbackSettingsPage from './pages/admin/feedback-settings.tsx';
import AdminImpersonatePage from './pages/admin/impersonate.tsx';
import AdminPartnersPage from './pages/admin/partners.tsx';
import AdminPublicDemoPage from './pages/admin/public-demo.tsx';
import AdminPartnerDetailPage from './pages/admin/partner-detail.tsx';
import AdminPartnerPayoutsPage from './pages/admin/partner-payouts.tsx';
import PartnerLayout, { RequirePartner } from './components/partner-layout.tsx';
import PartnerLoginPage from './pages/partner/login.tsx';
import PartnerOverviewPage from './pages/partner/overview.tsx';
import PartnerCustomersPage from './pages/partner/customers.tsx';
import PartnerCommissionsPage from './pages/partner/commissions.tsx';
import PartnerPayoutsPage from './pages/partner/payouts.tsx';
import PartnerClaimsPage from './pages/partner/claims.tsx';
import PartnerAccountPage from './pages/partner/account.tsx';
import PartnerSecurityPage from './pages/partner/security.tsx';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) return <LoadingBlock label={t('app.checkingSession')} />;
  if (!user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (
    user.role === 'crew' &&
    (location.pathname === '/settings/security' ||
      (location.pathname === '/settings' && location.hash === '#settings-security'))
  ) {
    return <Navigate to="/crew-account" replace />;
  }
  if (user.role !== 'crew' && /^\/my-jobs(?:\/|$)/.test(location.pathname)) return <Navigate to="/" replace />;
  if (
    user.role === 'crew' &&
    !/^\/(my-jobs(?:\/.*)?|chats|notifications|staff(?:\/(?:announcements|time-off|swaps|documents|photos|visit-offers|preferences))?|crew-account|swap-approval\/[^/]+)$/.test(
      location.pathname,
    )
  ) {
    const oldVisit = location.pathname.match(/^\/schedule\/visits\/([^/]+)/);
    const params = new URLSearchParams(location.search);
    const selectedVisit =
      oldVisit?.[1] ??
      (location.pathname === '/schedule' ? (params.get('appointment') ?? params.get('appointmentId')) : null);
    return <Navigate to={selectedVisit ? `/my-jobs/${encodeURIComponent(selectedVisit)}` : '/my-jobs'} replace />;
  }
  return <>{children}</>;
}

/**
 * `/magic-quote` → the Walkthroughs tab, search string intact: a link that
 * carried `?customerId=` is handed to "New quote" with Magic Quote chosen,
 * because that is the screen where a walkthrough now starts.
 */
function LegacyMagicQuoteRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.has('customerId') || params.has('propertyId')) {
    params.set('method', 'magic');
    return <Navigate to={`/quotes/new?${params.toString()}`} replace />;
  }
  return <Navigate to="/quotes/walkthroughs" replace />;
}

/**
 * `/change-orders` → `/extra-work`, search string intact. The screen was
 * renamed; a bookmark or a link pasted into a team chat still lands on it.
 */
function LegacyChangeOrdersRedirect() {
  const location = useLocation();
  return <Navigate to={`/extra-work${location.search}`} replace />;
}

/**
 * Owners and managers land on the dashboard; everyone else keeps landing on
 * the quotes list, because that is where an estimator's work actually is —
 * and the only page the dashboard's own permission would let them see anyway.
 */
function HomeRedirect() {
  const { can, company } = useSession();
  // Every plan has a dashboard: the full one on the paid plans, and on Free a
  // short one with what the plan does and what the company is owed — so a
  // company on Free lands on something it can use, not on a page that only
  // explains what it is missing.
  const hasDashboard = companyHasPlanFeature(company, 'quote_payments');
  // An estimator or read-only viewer has no `reports:read`, and lands on the
  // sales dashboard at the same address (pages/dashboard.tsx).
  const hasHome = can('reports:read') || can('quotes:read');
  return <Navigate to={hasHome && hasDashboard ? '/dashboard' : '/quotes'} replace />;
}

function RedirectIfSignedIn({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, isLoading, sessionConfirmed, mfaSetupRequired } = useSession();
  const location = useLocation();
  if (isLoading) return <LoadingBlock label={t('app.loading')} />;
  // Only on the server's own word, never on the remembered copy from local
  // storage. The remembered identity exists so RequireAuth can open the app
  // offline; letting it also bounce a visitor OFF this page means a stale
  // identity over a dead cookie is sent into the app, refused by the first
  // request, and sent straight back here — the sign-in screen flashing in a
  // cycle. This guard and RequireAuth already read the same `user`, so they
  // cannot argue with each other; this keeps a merely-remembered user from
  // taking the one turn that costs a round trip through the app. (The partner
  // pair pins down the same rule: RequirePartner leaves only on a 401,
  // pages/partner/login.tsx enters only on a fetched success.)
  //
  // Keep the normal landing decision in HomeRedirect so eligible users
  // reach the dashboard while role and plan fallbacks stay centralized.
  //
  // The same destination the sign-in pages pick themselves, not always "/":
  // when one of them signs somebody in, this guard re-renders with the new
  // session and its navigation overtakes the page's own (react-router runs
  // `navigate` as a transition). Sending "/" here dropped the deep link that
  // brought them to sign in, and walked a person the deployment requires to
  // add a second factor straight past Settings → Security (D-10).
  if (user && sessionConfirmed) return <Navigate to={signedInLanding(mfaSetupRequired, location.state)} replace />;
  return <>{children}</>;
}

/** Where a signed-in visitor of a sign-in page belongs; see RedirectIfSignedIn. */
function signedInLanding(mfaSetupRequired: boolean, state: unknown): string {
  if (mfaSetupRequired) return '/settings/security';
  return sameOriginPath((state as { from?: string } | null)?.from ?? null) ?? '/';
}

export default function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfSignedIn>
            <LoginPage />
          </RedirectIfSignedIn>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfSignedIn>
            <RegisterPage />
          </RedirectIfSignedIn>
        }
      />
      {/* Reached only by the OAuth callback, for an identity with no account yet.
          Behind RedirectIfSignedIn like the other entry points: someone who
          already has a session has nothing to finish here. */}
      <Route
        path="/complete-signup"
        element={
          <RedirectIfSignedIn>
            <CompleteSignupPage />
          </RedirectIfSignedIn>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/my-jobs" element={<MyJobsPage />} />
        <Route path="/my-jobs/:appointmentId" element={<CrewJobPage />} />
        <Route path="/staff" element={<StaffPage />} />
        {/* Announcements are read on the staff home (Updates) and written on
            /staff/manage; there is no page of their own. The address is still
            the natural one to type, and the API route is named the same, so it
            leads to the feed instead of "Page not found". */}
        <Route path="/staff/announcements" element={<Navigate to="/staff" replace />} />
        <Route path="/staff/preferences" element={<StaffPreferencesPage />} />
        <Route path="/staff/time-off" element={<StaffTimeOffPage />} />
        <Route path="/staff/swaps" element={<StaffSwapsPage />} />
        <Route path="/staff/manage" element={<StaffManagementPage />} />
        <Route path="/staff/documents" element={<StaffDocumentsPage />} />
        <Route path="/staff/visit-offers" element={<StaffVisitOffersPage />} />
        <Route path="/staff/photos" element={<StaffPhotosPage />} />
        <Route path="/crew-account" element={<CrewAccountPage />} />
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/margin-overrides" element={<MarginOverridesPage />} />
        <Route path="/reports/:section" element={<ReportSectionPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/new" element={<NewQuotePage />} />
        {/* Before `/quotes/:quoteId` only in the reader's mind — react-router
            ranks a static segment above a dynamic one whatever the order. */}
        <Route path="/quotes/walkthroughs" element={<WalkthroughsPage />} />
        <Route path="/quotes/photos" element={<PhotoQuotesPage />} />
        <Route path="/quotes/photos/demo" element={<PhotoQuoteDemoPage />} />
        <Route path="/quotes/:quoteId" element={<QuoteEditorPage />} />
        {/* Magic Quote's old front door, from when it had a menu entry of its
            own. Bookmarks, the docs' older links and the mobile app's habit of
            deep-linking here all still arrive; they are shown the Walkthroughs
            tab, which is where that screen went. The walkthrough and review
            routes below did not move — those ids are in notifications and
            saved links, and a URL that keeps working is worth more than a
            tidy prefix. */}
        <Route path="/magic-quote" element={<LegacyMagicQuoteRedirect />} />
        <Route path="/magic-quote/:sessionId" element={<MagicQuoteWalkthroughPage />} />
        <Route path="/magic-quote/:sessionId/review" element={<MagicQuoteReviewPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/extra-work" element={<ChangeOrdersPage />} />
        <Route path="/change-orders" element={<LegacyChangeOrdersRedirect />} />
        <Route path="/inspections" element={<InspectionsPage />} />
        <Route path="/inspections/:inspectionId" element={<InspectionDetailPage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/requests/photo/:intakeId" element={<PhotoQuoteReviewPage />} />
        <Route path="/logs" element={<LogsPage />} />
        <Route path="/work" element={<WorkHubPage />} />
        {/* The phone app calls its overflow screen More; one hub, so the
            other name leads to it. */}
        <Route path="/more" element={<Navigate to="/work" replace />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/inbox-approval/:id" element={<InboxApprovalPage />} />
        <Route path="/swap-approval/:id" element={<SwapApprovalPage />} />
        <Route path="/chats" element={<ChatsPage />} />
        <Route path="/checklist-templates" element={<ChecklistTemplatesPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/follow-ups" element={<CustomerFollowUpsPage />} />
        <Route path="/customers/import" element={<CustomerImportPage />} />
        <Route path="/customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="/customers/:customerId/properties/:propertyId/checklist" element={<PropertyChecklistPage />} />
        <Route path="/schedule/map" element={<LiveMapPage />} />
        <Route path="/customers/map" element={<LiveMapPage customers />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/today" element={<TodayPage />} />
        <Route path="/schedule/availability" element={<PlanningAvailabilityPage />} />
        <Route path="/schedule/change-requests" element={<ChangeRequestsPage />} />
        <Route path="/schedule/open-spots/:offerId" element={<OpenSpotPage />} />
        <Route path="/schedule/new" element={<NewSeriesPage />} />
        <Route path="/schedule/series/:seriesId" element={<SeriesDetailPage />} />
        <Route path="/schedule/visits/:appointmentId/work-order" element={<WorkOrderPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/feedback/:requestId" element={<FeedbackDetailPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/api" element={<ApiDocsPage />} />
        <Route path="/settings/pricing" element={<PricingHubPage />} />
        <Route path="/settings/pricing/base" element={<PricingBasePage />} />
        <Route path="/settings/pricing/area" element={<PricingAreaPage />} />
        <Route path="/settings/pricing/add-ons" element={<PricingAddOnsPage />} />
        <Route path="/settings/pricing/dust" element={<PricingDustPage />} />
        <Route path="/settings/pricing/walkthrough" element={<PricingWalkthroughPage />} />
        <Route path="/settings/pricing/numbering" element={<PricingNumberingPage />} />
        <Route path="/settings/messaging" element={<MessagingHubPage />} />
        <Route path="/settings/texting" element={<TextingSettingsPage />} />
        <Route path="/settings/calls" element={<CallsSettingsPage />} />
        <Route path="/settings/job-chat" element={<JobChatSettingsPage />} />
        <Route path="/settings/templates" element={<TemplatesHubPage />} />
        <Route path="/settings/templates/:family" element={<TemplatesHubPage />} />
        <Route path="/settings/team" element={<TeamHubPage />} />
        <Route path="/settings/team/saved" element={<TeamSavedPage />} />
        <Route path="/settings/team/swap-policy" element={<TeamSwapPolicyPage />} />
        <Route path="/settings/team-pay" element={<TeamPayPage />} />
        <Route path="/settings/account" element={<AccountPage />} />
        <Route path="/settings/security" element={<SecurityPage />} />
        <Route path="/settings/connected-accounts" element={<ConnectedAccountsPage />} />
        <Route path="/settings/integrations" element={<IntegrationsPage />} />
        <Route path="/settings/:sectionSlug" element={<SettingsLeafPage />} />
      </Route>

      {/* The operator CMS. Entirely outside the tenant session machinery:
          its own login, its own cookie, its own API. RequireAdmin is a UX
          gate only — the /api/admin stack is what actually refuses. */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        <Route index element={<AdminHomePage />} />
        <Route path="companies" element={<AdminCompaniesPage />} />
        <Route path="companies/:companyId" element={<AdminCompanyDetailPage />} />
        <Route path="impersonate" element={<AdminImpersonatePage />} />
        <Route path="discounts" element={<AdminDiscountsPage />} />
        <Route path="feedback" element={<AdminFeedbackPage />} />
        {/* The settings route is registered before the id route so
            /admin/feedback/settings is a screen rather than a request id. */}
        <Route path="feedback/settings" element={<AdminFeedbackSettingsPage />} />
        <Route path="feedback/:requestId" element={<AdminFeedbackDetailPage />} />
        <Route path="partners" element={<AdminPartnersPage />} />
        {/* The payouts route is registered before the id route so
            /admin/partners/payouts is a screen rather than a partner id. */}
        <Route path="partners/payouts" element={<AdminPartnerPayoutsPage />} />
        <Route path="partners/:affiliateId" element={<AdminPartnerDetailPage />} />
        <Route path="public-demo" element={<AdminPublicDemoPage />} />
        <Route path="admins" element={<AdminAdminsPage />} />
        {/* Where an operator with no factor is sent after sign-in, so the
            mandate has somewhere to lead. */}
        <Route path="security" element={<AdminSecurityPage />} />
      </Route>

      {/* The affiliate partner portal: a separate security realm with its own
          cookie, its own API and its own shell. RequirePartner is a UX gate
          only — the /api/partner stack is what actually refuses.

          Note the catch-all at the end. Every /partner/* path resolves inside
          this shell, so a mistyped or stale portal URL lands back on the
          portal's own overview instead of falling through to the tenant
          NotFound page — whose only way out is /quotes, a tenant-guarded route
          that bounces a partner straight into the app's sign-in redirect. */}
      <Route path="/partner/login" element={<PartnerLoginPage />} />
      <Route
        path="/partner"
        element={
          <RequirePartner>
            <PartnerLayout />
          </RequirePartner>
        }
      >
        <Route index element={<PartnerOverviewPage />} />
        <Route path="customers" element={<PartnerCustomersPage />} />
        <Route path="commissions" element={<PartnerCommissionsPage />} />
        <Route path="payouts" element={<PartnerPayoutsPage />} />
        <Route path="claims" element={<PartnerClaimsPage />} />
        <Route path="account" element={<PartnerAccountPage />} />
        <Route path="security" element={<PartnerSecurityPage />} />
        <Route path="*" element={<Navigate to="/partner" replace />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
